import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { addRecap, updateRecap, deleteRecap } from "@/lib/api";
import SectionHeading, { Reveal } from "./SectionHeading";

const pad = (n) => String(n).padStart(2, "0");

const EMPTY = { opponent: "", result: "", text: "", passcode: "" };

export default function Recaps({ recaps, onAdded, openerDate }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletePass, setDeletePass] = useState("");

  const opener = openerDate ? new Date(openerDate).getTime() : null;
  const showOpenerPrompt =
    opener && Date.now() >= opener && Date.now() <= opener + 14 * 86400000;

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({ opponent: r.opponent, result: r.result, text: r.text, passcode: "" });
    setOpen(true);
    document.querySelector("#recaps")?.scrollIntoView({ behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        await updateRecap(editingId, form);
        toast.success("Recap updated. Typo handled.");
      } else {
        await addRecap(form);
        toast.success("Recap posted. Coach approved.");
      }
      cancelEdit();
      setOpen(false);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save recap");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async (id) => {
    setBusy(true);
    try {
      await deleteRecap(id, deletePass);
      toast.success("Recap removed from the book.");
      setDeletingId(null);
      setDeletePass("");
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete recap");
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full border border-white/15 bg-black px-3 py-2.5 font-jbmono text-xs text-white outline-none transition-colors focus:border-neon";

  return (
    <section
      id="recaps"
      data-testid="section-recaps"
      className="mx-auto max-w-7xl px-6 py-24 md:py-36"
    >
      <SectionHeading
        number="04"
        title="GAME RECAPS"
        sub="Straight from the player"
        id="recaps"
      />

      {showOpenerPrompt && !open && (
        <Reveal>
          <div
            data-testid="opener-prompt"
            className="mb-8 flex flex-col gap-4 rounded-2xl border-2 border-neon bg-neon/10 p-6 shadow-[0_0_35px_rgba(57,255,20,0.2)] sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-center gap-4">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-neon opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-neon" />
              </span>
              <div>
                <p className="font-bebas text-3xl leading-none tracking-wide text-neon">
                  OPENING DAY IS HERE
                </p>
                <p className="mt-1 font-jbmono text-[11px] uppercase tracking-[0.2em] text-white/60">
                  Log the first 12UP recap while it's fresh
                </p>
              </div>
            </div>
            <button
              data-testid="opener-prompt-button"
              onClick={() => setOpen(true)}
              className="shrink-0 rounded-full bg-neon px-6 py-3 font-bebas text-lg tracking-widest text-black transition-shadow hover:shadow-[0_0_25px_rgba(57,255,20,0.5)]"
            >
              WRITE IT
            </button>
          </div>
        </Reveal>
      )}

      <Reveal>
        <button
          data-testid="recap-add-toggle"
          onClick={() => {
            if (open) cancelEdit();
            setOpen(!open);
          }}
          className="mb-10 flex items-center gap-3 rounded-full border border-neon/50 px-6 py-3 font-bebas text-lg tracking-widest text-neon transition-colors duration-300 hover:bg-neon hover:text-black"
        >
          {open ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {open ? "CLOSE THE NOTEBOOK" : "ADD A RECAP"}
        </button>
      </Reveal>

      {open && (
        <Reveal>
          <form
            data-testid="recap-form"
            onSubmit={submit}
            className="mb-14 grid gap-4 rounded-2xl border border-white/10 bg-panel p-7 md:grid-cols-2"
          >
            {editingId && (
              <div className="flex items-center justify-between md:col-span-2">
                <p className="font-jbmono text-[10px] uppercase tracking-[0.3em] text-hotpink">
                  Editing recap
                </p>
                <button
                  type="button"
                  data-testid="recap-edit-cancel"
                  onClick={cancelEdit}
                  className="font-jbmono text-[10px] tracking-[0.2em] text-white/50 underline underline-offset-4 hover:text-white"
                >
                  CANCEL EDIT
                </button>
              </div>
            )}
            <div>
              <label className="mb-1.5 block font-jbmono text-[10px] uppercase tracking-[0.25em] text-white/50">
                Opponent / Tournament
              </label>
              <input
                data-testid="recap-form-opponent"
                required
                className={field}
                placeholder="vs. River City Raptors"
                value={form.opponent}
                onChange={(e) => setForm({ ...form, opponent: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1.5 block font-jbmono text-[10px] uppercase tracking-[0.25em] text-white/50">
                Result
              </label>
              <input
                data-testid="recap-form-result"
                className={field}
                placeholder="W 9-4"
                value={form.result}
                onChange={(e) => setForm({ ...form, result: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1.5 block font-jbmono text-[10px] uppercase tracking-[0.25em] text-white/50">
                What happened? What did you learn?
              </label>
              <textarea
                data-testid="recap-form-text"
                required
                rows={3}
                maxLength={400}
                className={field}
                placeholder="2-3 sentences, your words..."
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1.5 block font-jbmono text-[10px] uppercase tracking-[0.25em] text-white/50">
                Family passcode
              </label>
              <input
                data-testid="recap-form-passcode"
                type="password"
                required
                className={field}
                value={form.passcode}
                onChange={(e) => setForm({ ...form, passcode: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <button
                data-testid="recap-form-submit"
                type="submit"
                disabled={busy}
                className="w-full rounded-full bg-neon py-3 font-bebas text-xl tracking-widest text-black transition-shadow hover:shadow-[0_0_25px_rgba(57,255,20,0.5)] disabled:opacity-50"
              >
                {busy ? "SAVING..." : editingId ? "SAVE CHANGES" : "POST RECAP"}
              </button>
            </div>
          </form>
        </Reveal>
      )}

      <div className="border-b border-white/10">
        {recaps.map((r, i) => (
          <Reveal key={r.id}>
            <article
              data-testid={`recap-${i + 1}`}
              className="group grid items-start gap-4 border-t border-white/10 py-10 md:grid-cols-[110px_1fr_auto]"
            >
              <span className="font-bebas text-6xl leading-none text-outline-neon">
                {pad(i + 1)}
              </span>
              <div>
                <p className="font-jbmono text-[10px] uppercase tracking-[0.3em] text-neon">
                  {new Date(r.created_at)
                    .toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                    .toUpperCase()}
                  {r.result ? ` · ${r.result}` : ""}
                </p>
                <h3 className="mt-2 font-bebas text-3xl tracking-wide transition-colors duration-300 group-hover:text-neon md:text-4xl">
                  {r.opponent}
                </h3>
                <p className="mt-3 max-w-2xl text-sm text-white/60 md:text-base">
                  {r.text}
                </p>
                {deletingId === r.id && (
                  <div
                    data-testid={`recap-delete-confirm-${i + 1}`}
                    className="mt-4 flex max-w-md items-center gap-3 rounded-full border border-hotpink/50 bg-hotpink/10 px-4 py-2.5"
                  >
                    <input
                      data-testid={`recap-delete-passcode-${i + 1}`}
                      type="password"
                      placeholder="Passcode to confirm"
                      className="w-full bg-transparent font-jbmono text-xs text-white outline-none placeholder:text-white/30"
                      value={deletePass}
                      onChange={(e) => setDeletePass(e.target.value)}
                    />
                    <button
                      data-testid={`recap-delete-confirm-button-${i + 1}`}
                      onClick={() => confirmDelete(r.id)}
                      disabled={busy}
                      className="shrink-0 font-jbmono text-[10px] tracking-[0.2em] text-hotpink hover:text-white"
                    >
                      DELETE
                    </button>
                    <button
                      data-testid={`recap-delete-cancel-${i + 1}`}
                      onClick={() => {
                        setDeletingId(null);
                        setDeletePass("");
                      }}
                      className="shrink-0 font-jbmono text-[10px] tracking-[0.2em] text-white/40 hover:text-white"
                    >
                      CANCEL
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                <button
                  data-testid={`recap-edit-${i + 1}`}
                  onClick={() => startEdit(r)}
                  aria-label="Edit recap"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/50 transition-colors hover:border-neon hover:text-neon"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  data-testid={`recap-delete-${i + 1}`}
                  onClick={() => setDeletingId(r.id)}
                  aria-label="Delete recap"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/50 transition-colors hover:border-hotpink hover:text-hotpink"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
